// src/services/tutorialService.ts
/**
 * Tutorial Service
 *
 * Manages tutorial state and user progress through onboarding
 */

export interface TutorialState {
  hasSeenOnboarding: boolean;
  hasCompletedFirstCombination: boolean;
  hasOpenedLibrary: boolean;
  hasVisitedAllModes: string[]; // Array of visited mode names
  tutorialStep: number; // Current step in interactive tutorial
}

const STORAGE_KEY = 'alchemistry_tutorial';

// Default tutorial state for new users
const DEFAULT_TUTORIAL_STATE: TutorialState = {
  hasSeenOnboarding: false,
  hasCompletedFirstCombination: false,
  hasOpenedLibrary: false,
  hasVisitedAllModes: [],
  tutorialStep: 0,
};

/**
 * Load tutorial state from localStorage
 */
export const loadTutorialState = (): TutorialState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return { ...DEFAULT_TUTORIAL_STATE, ...JSON.parse(saved) };
    }
  } catch (error) {
    console.error('Error loading tutorial state:', error);
  }
  return DEFAULT_TUTORIAL_STATE;
};

/**
 * Save tutorial state to localStorage
 */
export const saveTutorialState = (state: TutorialState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving tutorial state:', error);
  }
};

/**
 * Mark onboarding as complete
 */
export const markOnboardingComplete = (): void => {
  const state = loadTutorialState();
  state.hasSeenOnboarding = true;
  saveTutorialState(state);
};

/**
 * Mark first combination as complete
 */
export const markFirstCombinationComplete = (): void => {
  const state = loadTutorialState();
  state.hasCompletedFirstCombination = true;
  saveTutorialState(state);
};

/**
 * Mark library as opened
 */
export const markLibraryOpened = (): void => {
  const state = loadTutorialState();
  state.hasOpenedLibrary = true;
  saveTutorialState(state);
};

/**
 * Mark a game mode as visited
 */
export const markModeVisited = (mode: string): void => {
  const state = loadTutorialState();
  if (!state.hasVisitedAllModes.includes(mode)) {
    state.hasVisitedAllModes.push(mode);
    saveTutorialState(state);
  }
};

/**
 * Update tutorial step
 */
export const updateTutorialStep = (step: number): void => {
  const state = loadTutorialState();
  state.tutorialStep = step;
  saveTutorialState(state);
};

/**
 * Check if user should see tutorial
 */
export const shouldShowTutorial = (): boolean => {
  const state = loadTutorialState();
  return !state.hasSeenOnboarding;
};

/**
 * Check if user should see interactive walkthrough
 */
export const shouldShowWalkthrough = (): boolean => {
  const state = loadTutorialState();
  return state.hasSeenOnboarding && !state.hasCompletedFirstCombination;
};

/**
 * Reset tutorial state (for testing)
 */
export const resetTutorial = (): void => {
  saveTutorialState(DEFAULT_TUTORIAL_STATE);
};

/**
 * Get tutorial progress percentage
 */
export const getTutorialProgress = (): number => {
  const state = loadTutorialState();
  let completed = 0;
  const total = 4; // Total checkpoints

  if (state.hasSeenOnboarding) completed++;
  if (state.hasCompletedFirstCombination) completed++;
  if (state.hasOpenedLibrary) completed++;
  if (state.hasVisitedAllModes.length >= 3) completed++; // Visit at least 3 modes

  return Math.round((completed / total) * 100);
};
